import { Component } from '@theme/component';

const DPR = window.devicePixelRatio || 1;

/**
 * Mood/Weather PDP section, ported line-for-line from the theme's real original
 * per-product template: a full-bleed particle canvas (type/color/speed/drift all driven
 * by one static per-product config) plus a radial glow sit behind the mood copy. Only DOM
 * lookups are scoped to `this` instead of `document.getElementById` — the particle
 * physics and draw calls, including the per-type branching (spray/rain/snow/petals/
 * fireflies/dust/shimmer), are unchanged.
 * @extends {Component}
 */
class MoodWeatherComponent extends Component {
  requiredRefs = ['canvas', 'label', 'rule', 'content', 'season', 'name', 'desc', 'data'];

  connectedCallback() {
    super.connectedCallback();

    this._F = JSON.parse(this.refs.data.textContent);
    this._ctx = this.refs.canvas.getContext('2d');
    this._w = 0;
    this._h = 0;
    this._particles = [];
    this._running = false;
    this._raf = null;

    this._initWeather();

    this._resizeHandler = () => this._resize();
    window.addEventListener('resize', this._resizeHandler);

    // The particle loop only actually costs anything while this section is
    // on-screen — pausing it off-screen (same pattern as the ambient
    // fragrance-pyramid background and the note cards) is what keeps a
    // section this far down the page from burning main-thread time during
    // the page's critical load window, well before anyone's scrolled to it.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this._running = entry.isIntersecting;
          if (this._running && !this._raf) this._loop();
        }
      },
      { threshold: 0.1 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this._resizeHandler);
    this.observer?.disconnect();
    if (this._raf) cancelAnimationFrame(this._raf);
    this._destroyed = true;
  }

  _loop() {
    const tick = () => {
      if (!this._running || this._destroyed) {
        this._raf = null;
        return;
      }
      this._draw();
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
  }

  _resize() {
    this._w = this.offsetWidth || window.innerWidth;
    this._h = this.scrollHeight || 300;
    this.refs.canvas.width = Math.ceil(this._w * DPR);
    this.refs.canvas.height = Math.ceil(this._h * DPR);
    this.refs.canvas.style.width = `${this._w}px`;
    this.refs.canvas.style.height = `${this._h}px`;
    this._ctx.setTransform(1, 0, 0, 1, 0, 0);
    this._ctx.scale(DPR, DPR);
    this._spawnParticles();
  }

  _spawnParticles() {
    if (!this._w || !this._h) return;
    const p = this._F.particles;
    this._particles = [];
    for (let i = 0; i < p.count; i++) {
      const s = p.size[0] + Math.random() * (p.size[1] - p.size[0]);
      this._particles.push({
        x: Math.random() * this._w,
        y: Math.random() * this._h,
        vx: (Math.random() - 0.5) * p.drift,
        vy: p.type === 'rain' ? p.speed * (0.6 + Math.random() * 0.4) : -(p.speed * (0.4 + Math.random() * 0.6)),
        size: s,
        alpha: 0.08 + Math.random() * 0.55,
        phase: Math.random() * Math.PI * 2,
      });
    }
  }

  _draw() {
    if (!this._w || !this._h) return;
    const ctx = this._ctx;
    const w = this._w;
    const h = this._h;
    ctx.clearRect(0, 0, w, h);

    const weather = this._F;
    const p = weather.particles;
    const c = p.color;

    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, weather.bg[0]);
    bg.addColorStop(0.5, weather.bg[1]);
    bg.addColorStop(1, weather.bg[2]);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    const g = weather.glow;
    const glow = ctx.createRadialGradient(w * g.x, h * g.y, 0, w * g.x, h * g.y, w * g.r);
    glow.addColorStop(0, g.color);
    glow.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, h);

    this._particles.forEach((pt) => {
      pt.phase += 0.012;
      if (p.type === 'snow' || p.type === 'shimmer' || p.type === 'dust') {
        pt.x += Math.sin(pt.phase) * 0.4 + pt.vx;
        pt.y += pt.vy;
        if (pt.y < -10) {
          pt.y = h + 10;
          pt.x = Math.random() * w;
        }
      } else if (p.type === 'rain') {
        pt.x += pt.vx * 0.4;
        pt.y += p.speed * 0.5;
        if (pt.y > h + 10) {
          pt.y = -10;
          pt.x = Math.random() * w;
        }
      } else if (p.type === 'petals') {
        pt.x += Math.sin(pt.phase) * pt.vx * 0.6 + 0.25;
        pt.y += p.speed * 0.5 * (0.5 + Math.sin(pt.phase * 0.5) * 0.3);
        if (pt.y > h + 20) {
          pt.y = -20;
          pt.x = Math.random() * w;
        }
      } else if (p.type === 'fireflies') {
        pt.x += Math.sin(pt.phase * 0.5) * 0.8 + pt.vx * 0.5;
        pt.y += Math.cos(pt.phase * 0.4) * 0.5;
        if (pt.x < 0) pt.x = w;
        if (pt.x > w) pt.x = 0;
        if (pt.y < 0) pt.y = h;
        if (pt.y > h) pt.y = 0;
      } else if (p.type === 'spray') {
        pt.x += Math.sin(pt.phase * 1.2) * pt.vx * 1.2;
        pt.y += pt.vy * 0.7;
        if (pt.y < -10) {
          pt.y = h * 0.5 + Math.random() * h * 0.5;
          pt.x = Math.random() * w;
        }
      }

      const a = pt.alpha * (0.5 + 0.5 * Math.sin(pt.phase));
      ctx.beginPath();
      if (p.type === 'rain') {
        ctx.strokeStyle = `rgba(${c[0]},${c[1]},${c[2]},${a})`;
        ctx.lineWidth = pt.size * 0.4;
        ctx.moveTo(pt.x, pt.y);
        ctx.lineTo(pt.x + pt.vx * 1.2, pt.y + 5);
        ctx.stroke();
      } else if (p.type === 'fireflies') {
        const gf = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, pt.size * 3);
        gf.addColorStop(0, `rgba(${c[0]},${c[1]},${c[2]},${a})`);
        gf.addColorStop(1, `rgba(${c[0]},${c[1]},${c[2]},0)`);
        ctx.fillStyle = gf;
        ctx.arc(pt.x, pt.y, pt.size * 3, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${a})`;
        ctx.arc(pt.x, pt.y, pt.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.beginPath();
    });
  }

  _initWeather() {
    const w = this._F;
    this.refs.label.style.color = w.labelColor;
    this.refs.rule.style.background = w.ruleColor;
    this.refs.season.textContent = w.season;
    this.refs.season.style.color = w.labelColor;
    this.refs.name.innerHTML = w.name.replace('\n', '<br>');
    this.refs.name.style.color = w.nameColor;
    this.refs.desc.textContent = w.desc;
    this.refs.desc.style.color = w.descColor;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._resize();
        this._draw(); // one static frame so the section isn't blank before it scrolls into view
        window.setTimeout(() => this.refs.content.classList.add('is-visible'), 1000);
      });
    });
  }
}

if (!customElements.get('mood-weather-component')) {
  customElements.define('mood-weather-component', MoodWeatherComponent);
}
